import { memo, useId } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import Button from '../Button.jsx';
import Tooltip from '../Tooltip.jsx';

/**
 * Design-system "Stepper Nav Footer": a 72px bar pinned to the bottom of the
 * scrollport with a top separator. Back (or Cancel on the first step) sits on
 * the left; the primary action — whose label changes on the last step — sits on
 * the right, optionally preceded by a secondary action.
 */
const BAR_CLASS = [
  'sticky bottom-0 z-10 mt-auto flex h-[72px] w-full shrink-0 items-center justify-between gap-3',
  'border-t border-edge bg-surface-base px-6',
].join(' ');

/**
 * Navigation bar of a multi-step flow. Pair it with {@link Stepper}: the flow
 * decides what Back and the primary action do and when the primary is allowed.
 *
 * - `backVariant="back"` (default) draws an arrow; use `"cancel"` on the first
 *   step, where the left button leaves the flow instead of going back.
 * - A blocked primary action can say why with `primaryDisabledReason`: the text
 *   is read by assistive technology and shown as a tooltip. It is only exposed
 *   while the button is actually disabled.
 * - `primaryLoading` blocks the primary action only; `busy` blocks both buttons
 *   (a request is in flight and leaving would orphan it).
 * - `secondaryAction` is a slot for one extra control before the primary one,
 *   e.g. "Create and create new" next to "Create and open".
 *
 * @param {object} props
 * @param {string} [props.ariaLabel] — Accessible name of the navigation landmark.
 * @param {string} [props.backLabel] — Label of the left button; omit it to render no left button.
 * @param {() => void} [props.onBack]
 * @param {'back'|'cancel'} [props.backVariant='back']
 * @param {import('react').ReactNode} props.primaryLabel
 * @param {() => void} [props.onPrimary]
 * @param {import('lucide-react').LucideIcon|null} [props.primaryIcon=ArrowRight] — Pass `null` for none.
 * @param {boolean} [props.primaryDisabled=false]
 * @param {string} [props.primaryDisabledReason] — Why the primary action is blocked.
 * @param {boolean} [props.primaryLoading=false]
 * @param {boolean} [props.busy=false]
 * @param {import('react').ReactNode} [props.secondaryAction]
 * @param {string} [props.className] — Classes for the outer `<nav>`.
 */
function StepperNavFooter({
  ariaLabel,
  backLabel,
  onBack,
  backVariant = 'back',
  primaryLabel,
  onPrimary,
  primaryIcon = ArrowRight,
  primaryDisabled = false,
  primaryDisabledReason,
  primaryLoading = false,
  busy = false,
  secondaryAction,
  className = '',
}) {
  const reasonId = useId();
  const isPrimaryBlocked = primaryDisabled || busy;
  const reason = primaryDisabled ? primaryDisabledReason : undefined;
  const hasReason = Boolean(reason);

  const primary = (
    <Button
      variant="primary"
      rightIcon={primaryIcon ?? undefined}
      disabled={isPrimaryBlocked}
      loading={primaryLoading}
      aria-describedby={hasReason ? reasonId : undefined}
      onClick={onPrimary}
    >
      {primaryLabel}
    </Button>
  );

  return (
    <nav aria-label={ariaLabel} className={[BAR_CLASS, className].filter(Boolean).join(' ')}>
      {backLabel ? (
        <Button
          variant="secondary"
          leftIcon={backVariant === 'back' ? ArrowLeft : undefined}
          disabled={busy}
          onClick={onBack}
        >
          {backLabel}
        </Button>
      ) : (
        <span aria-hidden="true" />
      )}
      <div className="flex items-center gap-3">
        {secondaryAction}
        {hasReason ? (
          <>
            <Tooltip content={reason} size="compact" position="top" align="end">
              {primary}
            </Tooltip>
            <span id={reasonId} className="sr-only">
              {reason}
            </span>
          </>
        ) : (
          primary
        )}
      </div>
    </nav>
  );
}

StepperNavFooter.propTypes = {
  ariaLabel: PropTypes.string,
  backLabel: PropTypes.string,
  onBack: PropTypes.func,
  backVariant: PropTypes.oneOf(['back', 'cancel']),
  primaryLabel: PropTypes.node.isRequired,
  onPrimary: PropTypes.func,
  primaryIcon: PropTypes.elementType,
  primaryDisabled: PropTypes.bool,
  primaryDisabledReason: PropTypes.string,
  primaryLoading: PropTypes.bool,
  busy: PropTypes.bool,
  secondaryAction: PropTypes.node,
  className: PropTypes.string,
};

export default memo(StepperNavFooter);
